
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Html5Qrcode } from "html5-qrcode";
import { t } from '../locales';
import { Language } from '../types';

interface BarcodeScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDetected: (barcode: string) => void;
  language: Language;
}

const BarcodeScannerModal: React.FC<BarcodeScannerModalProps> = ({ isOpen, onClose, onDetected, language }) => {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [error, setError] = useState<string>('');
  const isScanning = useRef(false);

  useEffect(() => {
    if (isOpen && !scannerRef.current) {
        startScanning();
    }
    return () => {
        stopScanning();
    };
  }, [isOpen]);

  const startScanning = async () => {
    try {
        setError('');
        const html5QrCode = new Html5Qrcode("reader");
        scannerRef.current = html5QrCode;
        isScanning.current = true;

        await html5QrCode.start(
            { facingMode: "environment" }, 
            {
                fps: 10,
                qrbox: { width: 250, height: 250 },
                aspectRatio: 1.0,
                // Attempt to force continuous focus if supported
                videoConstraints: {
                    facingMode: "environment", // Explicitly reinforce environment
                    focusMode: "continuous",
                    width: { min: 640, ideal: 1280, max: 1920 },
                    height: { min: 480, ideal: 720, max: 1080 },
                }
            },
            (decodedText) => {
                // Success callback
                if (isScanning.current) {
                    isScanning.current = false;
                    html5QrCode.stop().then(() => {
                        scannerRef.current = null;
                        onDetected(decodedText);
                    });
                }
            },
            (errorMessage) => {
                // Ignore parse errors, they happen every frame no code is found
            }
        );
    } catch (err) {
        console.error("Error starting scanner", err);
        setError("Camera access required to scan barcodes.");
    }
  };

  const stopScanning = async () => {
      isScanning.current = false;
      if (scannerRef.current) {
          try {
              await scannerRef.current.stop();
              scannerRef.current = null;
          } catch (e) {
              console.warn("Error stopping scanner", e);
          }
      }
  };

  const handleClose = () => {
      stopScanning().then(onClose);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[80] bg-black flex flex-col justify-center">
        {/* Close Button Top Right */}
        <button 
            onClick={handleClose}
            className="absolute top-4 right-4 z-50 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
        >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
        </button>

        <div className="w-full max-w-md mx-auto relative">
            <div id="reader" className="w-full overflow-hidden rounded-lg bg-black"></div>
            
            {/* Overlay UI */}
            <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center">
                <div className="w-64 h-64 border-2 border-emerald-500/50 rounded-lg relative">
                    <div className="absolute top-0 left-0 w-4 h-4 border-t-4 border-l-4 border-emerald-500 -mt-1 -ml-1"></div>
                    <div className="absolute top-0 right-0 w-4 h-4 border-t-4 border-r-4 border-emerald-500 -mt-1 -mr-1"></div>
                    <div className="absolute bottom-0 left-0 w-4 h-4 border-b-4 border-l-4 border-emerald-500 -mb-1 -ml-1"></div>
                    <div className="absolute bottom-0 right-0 w-4 h-4 border-b-4 border-r-4 border-emerald-500 -mb-1 -mr-1"></div>
                </div>
                <p className="mt-8 text-white font-medium bg-black/50 px-4 py-1 rounded-full text-sm">
                    {error || "Scan a product barcode"}
                </p>
            </div>
        </div>

        <div className="absolute bottom-10 left-0 right-0 flex justify-center pb-safe">
            <button 
                onClick={handleClose}
                className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white font-bold rounded-xl shadow-lg border border-gray-700"
            >
                {t(language, 'cancel')}
            </button>
        </div>
    </div>,
    document.body
  );
};

export default BarcodeScannerModal;
